/**
 * Zustand Widget Store
 * Central state management for widget configuration, runtime state, and user session
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  WidgetConfig,
  WidgetState,
  Provider,
  AppointmentSlot,
  Service,
  Clinician,
  Location,
  Practice,
  WidgetSettings,
  PaymentMethod,
} from '@/types';
import { SAMPLE_PROVIDERS, getAllSampleSlots } from '@/mocks/sampleData';

/**
 * Complete widget store state
 */
interface WidgetStoreState extends WidgetState {
  /** Widget configuration */
  config: WidgetConfig | null;

  /** Widget settings from API */
  widgetSettings: WidgetSettings | null;

  /** Practice information */
  practice: Practice | null;

  /** Available services */
  services: Service[];

  /** Available clinicians */
  clinicians: Clinician[];

  /** Available locations */
  locations: Location[];

  /** Available payment methods */
  paymentMethods: PaymentMethod[];

  /** Available providers (legacy - for backward compatibility) */
  providers: Provider[];

  /** Available appointment slots */
  slots: AppointmentSlot[];

  /** Selected provider */
  selectedProvider: Provider | null;

  /** Selected slot */
  selectedSlot: AppointmentSlot | null;

  /** Current view/step */
  currentView: WidgetView;
}

/**
 * Widget view/step enumeration
 */
export enum WidgetView {
  LOADING = 'loading',
  PROVIDER_SELECTION = 'provider_selection',
  SLOT_SELECTION = 'slot_selection',
  PATIENT_INFO = 'patient_info',
  CONFIRMATION = 'confirmation',
  SUCCESS = 'success',
  ERROR = 'error',
}

/**
 * Widget store actions
 */
interface WidgetStoreActions {
  // Initialization
  initialize: (config: WidgetConfig) => void;
  reset: () => void;

  // State updates
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setHeight: (height: number) => void;
  setWidth: (width: number) => void;

  // Session management
  setSessionToken: (token: string | null) => void;
  setRecaptchaToken: (token: string | null) => void;

  // Widget data management
  setWidgetData: (data: {
    widgetSettings: WidgetSettings;
    practice: Practice;
    services: Service[];
    clinicians: Clinician[];
    locations: Location[];
    paymentMethods: PaymentMethod[];
  }) => void;

  // Provider and slot management
  setProviders: (providers: Provider[]) => void;
  setSlots: (slots: AppointmentSlot[]) => void;
  selectProvider: (provider: Provider | null) => void;
  selectSlot: (slot: AppointmentSlot | null) => void;

  // Navigation
  setView: (view: WidgetView) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}

/**
 * Complete widget store type
 */
type WidgetStore = WidgetStoreState & WidgetStoreActions;

/**
 * Initial widget state
 */
const initialState: WidgetStoreState = {
  // Configuration
  config: null,

  // Runtime state
  initialized: false,
  loading: false,
  error: null,
  height: 600,
  width: 0,

  // Session
  sessionToken: null,
  recaptchaToken: null,

  // Widget data from API
  widgetSettings: null,
  practice: null,
  services: [],
  clinicians: [],
  locations: [],
  paymentMethods: [],

  // Data (legacy)
  providers: [],
  slots: [],
  selectedProvider: null,
  selectedSlot: null,

  // View
  currentView: WidgetView.LOADING,
};

/**
 * Widget store with Zustand
 */
export const useWidgetStore = create<WidgetStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Initialization
      initialize: (config: WidgetConfig) => {
        // Load sample data for development
        const providers = SAMPLE_PROVIDERS;
        const slots = getAllSampleSlots();

        set(
          {
            config,
            initialized: true,
            loading: false,
            error: null,
            providers,
            slots,
            currentView: WidgetView.PROVIDER_SELECTION,
          },
          false,
          'widget/initialize'
        );
      },

      reset: () => {
        set(
          {
            ...initialState,
            config: get().config, // Keep configuration
          },
          false,
          'widget/reset'
        );
      },

      // State updates
      setLoading: (loading: boolean) => {
        set({ loading }, false, 'widget/setLoading');
      },

      setError: (error: string | null) => {
        set(
          {
            error,
            loading: false,
            currentView: error ? WidgetView.ERROR : get().currentView,
          },
          false,
          'widget/setError'
        );
      },

      clearError: () => {
        set({ error: null }, false, 'widget/clearError');
      },

      setHeight: (height: number) => {
        set({ height }, false, 'widget/setHeight');
      },

      setWidth: (width: number) => {
        set({ width }, false, 'widget/setWidth');
      },

      // Session management
      setSessionToken: (sessionToken: string | null) => {
        set({ sessionToken }, false, 'widget/setSessionToken');
      },

      setRecaptchaToken: (recaptchaToken: string | null) => {
        set({ recaptchaToken }, false, 'widget/setRecaptchaToken');
      },

      // Widget data management
      setWidgetData: (data) => {
        set(
          {
            widgetSettings: data.widgetSettings,
            practice: data.practice,
            services: data.services,
            clinicians: data.clinicians,
            locations: data.locations,
            paymentMethods: data.paymentMethods,
          },
          false,
          'widget/setWidgetData'
        );
      },

      // Provider and slot management
      setProviders: (providers: Provider[]) => {
        set({ providers }, false, 'widget/setProviders');
      },

      setSlots: (slots: AppointmentSlot[]) => {
        set({ slots }, false, 'widget/setSlots');
      },

      selectProvider: (selectedProvider: Provider | null) => {
        set(
          {
            selectedProvider,
            selectedSlot: null, // Reset slot when provider changes
            // Don't clear slots - they're filtered by the SlotSelection component
          },
          false,
          'widget/selectProvider'
        );
      },

      selectSlot: (selectedSlot: AppointmentSlot | null) => {
        set({ selectedSlot }, false, 'widget/selectSlot');
      },

      // Navigation
      setView: (currentView: WidgetView) => {
        set({ currentView }, false, 'widget/setView');
      },

      goToNextStep: () => {
        const currentView = get().currentView;
        let nextView: WidgetView;

        switch (currentView) {
          case WidgetView.PROVIDER_SELECTION:
            nextView = WidgetView.SLOT_SELECTION;
            break;
          case WidgetView.SLOT_SELECTION:
            nextView = WidgetView.PATIENT_INFO;
            break;
          case WidgetView.PATIENT_INFO:
            nextView = WidgetView.CONFIRMATION;
            break;
          case WidgetView.CONFIRMATION:
            nextView = WidgetView.SUCCESS;
            break;
          default:
            nextView = currentView;
        }

        set({ currentView: nextView }, false, 'widget/goToNextStep');
      },

      goToPreviousStep: () => {
        const currentView = get().currentView;
        let previousView: WidgetView;

        switch (currentView) {
          case WidgetView.SLOT_SELECTION:
            previousView = WidgetView.PROVIDER_SELECTION;
            break;
          case WidgetView.PATIENT_INFO:
            previousView = WidgetView.SLOT_SELECTION;
            break;
          case WidgetView.CONFIRMATION:
            previousView = WidgetView.PATIENT_INFO;
            break;
          default:
            previousView = currentView;
        }

        set({ currentView: previousView }, false, 'widget/goToPreviousStep');
      },
    }),
    {
      name: 'widget-store',
      enabled: import.meta.env.MODE === 'development',
    }
  )
);

/**
 * Selector hooks for optimized component rendering
 */
export const useWidgetConfig = () => useWidgetStore((state) => state.config);
export const useWidgetLoading = () => useWidgetStore((state) => state.loading);
export const useWidgetError = () => useWidgetStore((state) => state.error);
export const useCurrentView = () => useWidgetStore((state) => state.currentView);
export const useSelectedProvider = () => useWidgetStore((state) => state.selectedProvider);
export const useSelectedSlot = () => useWidgetStore((state) => state.selectedSlot);
